import { ProductRepositoryError } from './errors';

/** Versioned IndexedDB schema for local personal products (+ package photos). */
export const LOCAL_PRODUCTS_DB_NAME = 'fbc-local-products';
/** v1: products. v2: images. v3: device-local blend history. */
export const LOCAL_PRODUCTS_DB_VERSION = 3;
export const LOCAL_PRODUCTS_STORE = 'products';
export const LOCAL_PRODUCT_IMAGES_STORE = 'productImages';
export const LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX = 'byProductId';
export const LOCAL_BLEND_HISTORY_STORE = 'blendHistory';

export type IdbFactory = IDBFactory;

export function requireIndexedDb(factory?: IdbFactory): IdbFactory {
  const idb = factory ?? globalThis.indexedDB;
  if (!idb) {
    throw ProductRepositoryError.storage(
      'IndexedDB is not available in this environment.',
    );
  }
  return idb;
}

/**
 * Open the shared local-products database.
 * Upgrades add stores without touching existing personal records.
 * Preserves blocked-open rejection and late-success connection cleanup.
 */
export function openLocalProductsDb(
  factory?: IdbFactory,
): Promise<IDBDatabase> {
  const idb = requireIndexedDb(factory);
  return new Promise((resolve, reject) => {
    const req = idb.open(LOCAL_PRODUCTS_DB_NAME, LOCAL_PRODUCTS_DB_VERSION);
    let settled = false;
    const rejectOnce = (error: ProductRepositoryError): void => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    req.onerror = () =>
      rejectOnce(
        ProductRepositoryError.storage(
          req.error?.message ?? 'IndexedDB open failed',
        ),
      );
    req.onblocked = () =>
      rejectOnce(
        ProductRepositoryError.storage(
          'IndexedDB open blocked by another database connection',
        ),
      );
    req.onupgradeneeded = () => {
      applyLocalProductsSchemaUpgrade(req.result);
    };
    req.onsuccess = () => {
      if (settled) {
        req.result.close();
        return;
      }
      settled = true;
      resolve(req.result);
    };
  });
}

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        ProductRepositoryError.storage(
          request.error?.message ?? 'IndexedDB request failed',
        ),
      );
  });
}

export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () =>
      reject(
        ProductRepositoryError.storage(
          tx.error?.message ?? 'IndexedDB transaction failed',
        ),
      );
    tx.onabort = () =>
      reject(
        ProductRepositoryError.storage(
          tx.error?.message ?? 'IndexedDB transaction aborted',
        ),
      );
  });
}

/**
 * Apply the same upgrade path used in production opens (for upgrade tests).
 * Never deletes the products store.
 */
export function applyLocalProductsSchemaUpgrade(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains(LOCAL_PRODUCTS_STORE)) {
    db.createObjectStore(LOCAL_PRODUCTS_STORE, { keyPath: 'id' });
  }
  if (!db.objectStoreNames.contains(LOCAL_PRODUCT_IMAGES_STORE)) {
    const images = db.createObjectStore(LOCAL_PRODUCT_IMAGES_STORE, {
      keyPath: 'id',
    });
    images.createIndex(LOCAL_PRODUCT_IMAGES_BY_PRODUCT_INDEX, 'productId', {
      unique: false,
    });
  }
  if (!db.objectStoreNames.contains(LOCAL_BLEND_HISTORY_STORE)) {
    db.createObjectStore(LOCAL_BLEND_HISTORY_STORE, { keyPath: 'id' });
  }
}
